// src/calculators/labor2.js — дванадесетте панела на Фаза 5 (останалата
// част от каталога по КТ). Нормативната сметка е в services/{dismissal,
// worktime,family}.js — обща за тестове и екран. Новото тук: всеки панел
// носи странична колона „Кога се използва" + цитираните текстове
// (lawSide/withLaw) — изрично изискване на собственика.
//
// Цитатите са сверени в актуалната редакция към 08.2026 — вкл. новата
// формулировка на чл. 226 (електронен трудов запис, 01.06.2025) и
// чл. 40, ал. 5 КСО (ДВА работни дни от 01.01.2024).

import { el, clear, field, input, row } from '../shared/dom.js';
import { money } from '../shared/format.js';
import { dateInput, localTodayIso } from '../shared/dates.js';
import {
  unfairDismissalPay, unregisteredTerminationPay, payDeadline, claimDeadline,
  probationEnd, CLAIM_KINDS,
} from '../../services/dismissal.js';
import {
  nightUplift, nightHoursInShift, sirvBalance, holidayWorkPay, employerSickPay,
} from '../../services/worktime.js';
import { maternityTimeline, unpaidLeaveSplit } from '../../services/family.js';

const okIso = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s || '');
const bg = (iso) => String(iso || '').split('-').reverse().join('.');
const eur = (x) => money(Math.round((Number(x) || 0) * 100) / 100, 'EUR');
const line = (label, value, big) => el('div', { class: 'calc-line' + (big ? ' big' : '') }, el('span', { text: label }), el('span', { text: value }));
const noteRow = (t) => el('div', { style: 'font-size:12px;color:var(--muted);padding:2px 0', text: '• ' + t });
const checkRow = (box, text) => el('div', { class: 'field' }, el('label', { class: 'choice' }, box, el('span', { text })));

// Страничната колона „Кога се използва / Какво казва законът" е ОБЩА за
// калкулаторите — живее в ./law.js (изнесена в задача 4/6, без копие тук).
// hintify закача цитат при ключово поле (задача 6/6); lawHint — direktno
// при заглавие, когато панелът е справочник без полета.
import { withLaw, hintify, lawHint } from './law.js';

// ---- А4. Незаконно уволнение (чл. 225 КТ) -----------------------------------

export function buildUnfairDismissalCalc() {
  const f = {
    gross: input({ type: 'number', step: '0.01', min: '0', placeholder: 'напр. 1000' }),
    months: input({ type: 'number', step: '1', min: '0', value: '6' }),
    lower: el('input', { type: 'checkbox' }),
    lowerGross: input({ type: 'number', step: '0.01', min: '0', placeholder: 'заплатата на новата работа' }),
  };
  const lowerField = field('БТВ на по-нископлатената работа', f.lowerGross, { size: 's' });
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  function recompute() {
    clear(result); clear(notesBox);
    lowerField.style.display = f.lower.checked ? '' : 'none';
    const gross = Number(f.gross.value) || 0;
    if (gross <= 0) { result.appendChild(el('div', { class: 'calc-line', text: 'Въведете брутното възнаграждение (базата по чл. 228, ал. 1).' })); return; }
    const r = unfairDismissalPay({
      monthlyGross: gross, monthsOutOfWork: Number(f.months.value) || 0,
      lowerPaid: f.lower.checked, lowerPaidMonthly: Number(f.lowerGross.value) || 0,
    });
    result.appendChild(line('Месеци, за които се дължи', String(r.monthsPaid) + (r.capped ? ' (таванът по чл. 225)' : '')));
    result.appendChild(line(f.lower.checked ? 'Разлика на месец' : 'База на месец', eur(r.perMonth)));
    result.appendChild(line('Обезщетение', eur(r.amount), true));
    r.notes.forEach((n) => notesBox.appendChild(noteRow(n)));
  }
  Object.values(f).forEach((i) => { i.addEventListener('input', recompute); i.addEventListener('change', recompute); });
  recompute();
  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Незаконно уволнение — обезщетение (чл. 225 КТ)' }),
    row(hintify(field('Брутно възнаграждение (месец)', f.gross, { size: 's' }),
      'чл. 228, ал. 1 КТ', 'Базата е полученото БТВ за месеца, предхождащ месеца на основанието, или последното получено месечно БТВ.'),
      field('Месеци без работа', f.months, { size: 's' })),
    checkRow(f.lower, 'Работил е на ПО-НИСКОПЛАТЕНА работа (ал. 2 — дължи се разликата)'),
    lowerField,
    result, notesBox);
  return withLaw(panel, {
    when: [
      'Съдът е признал уволнение за незаконно (иск по чл. 344, ал. 1) и клиентът-работодател трябва да плати.',
      'Служител на клиент пита какво може да претендира след отменено уволнение.',
      'Останал е без работа изцяло — или е започнал по-нископлатена работа (тогава се дължи само разликата).',
      'Начисляването минава през ведомостта: върху обезщетението се дължат осигуровки и данък, а времето е стаж.',
    ],
    quotes: [
      { ref: 'чл. 225, ал. 1 КТ', text: 'При незаконно уволнение работникът или служителят има право на обезщетение от работодателя в размер на брутното му трудово възнаграждение за времето, през което е останал без работа поради това уволнение, но за не повече от 6 месеца.' },
      { ref: 'чл. 225, ал. 2 КТ', text: 'Ако през време на горния срок работникът или служителят е работил на по-нископлатена работа, той има право на разликата в заплатите.' },
      { ref: 'чл. 228, ал. 1 КТ (базата)', text: 'Брутното трудово възнаграждение за определяне на обезщетенията по този раздел е полученото от работника или служителя брутно трудово възнаграждение за месеца, предхождащ месеца, в който е възникнало основанието за съответното обезщетение, или последното получено от работника или служителя месечно брутно трудово възнаграждение…' },
    ],
  });
}

// ---- А7. Невписано прекратяване / документи (чл. 226 КТ) --------------------

export function buildUnregisteredTerminationCalc() {
  const f = {
    gross: input({ type: 'number', step: '0.01', min: '0', placeholder: 'напр. 1050' }),
    days: input({ type: 'number', step: '1', min: '1', value: '21' }),
    from: dateInput(''), to: dateInput(''),
  };
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  function recompute() {
    clear(result); clear(notesBox);
    const from = f.from.dataset.iso, to = f.to.dataset.iso;
    if (!(Number(f.gross.value) > 0) || !okIso(from) || !okIso(to) || from > to) {
      result.appendChild(el('div', { class: 'calc-line', text: 'Попълнете базата и периода на забавата (от прекратяването до вписването).' }));
      return;
    }
    const r = unregisteredTerminationPay({ baseMonthlyGross: Number(f.gross.value), baseWorkDays: Number(f.days.value) || 1, from, to });
    result.appendChild(line('Дневна база', eur(r.dailyRate)));
    result.appendChild(line('Работни дни на забавата', String(r.workDays)));
    result.appendChild(line('Обезщетение', eur(r.amount), true));
    r.notes.forEach((n) => notesBox.appendChild(noteRow(n)));
  }
  Object.values(f).forEach((i) => { i.addEventListener('input', recompute); i.addEventListener('change', recompute); });
  recompute();
  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Невписано прекратяване / забавени документи (чл. 226 КТ)' }),
    row(hintify(field('БТВ на базовия месец', f.gross, { size: 's' }),
      'чл. 226, ал. 3 КТ (ред. от 01.06.2025)', 'Обезщетението обхваща БТВ от деня на прекратяването до вписването му по чл. 62, ал. 3.'),
      field('Отработени дни в базовия месец', f.days, { size: 's' })),
    row(field('От (ден на прекратяването)', f.from, { size: 's' }), field('До (ден на вписването)', f.to, { size: 's' })),
    result, notesBox);
  return withLaw(panel, {
    when: [
      'Прекратяването не е подадено в НАП по чл. 62, ал. 3 и човекът не може да се регистрира в Бюрото по труда или да започне другаде.',
      'Работодател бави издаване на документи за стаж/доход или не е оформил стара хартиена книжка (те се връщат до 01.06.2026).',
      'От 01.06.2025 хартиената книжка е заменена от електронен трудов запис — затова законът вече говори за „вписване", не за „предаване на книжката".',
    ],
    quotes: [
      { ref: 'чл. 226, ал. 2 КТ (ред. от 01.06.2025)', text: 'Работодателят и виновните длъжностни лица отговарят солидарно към работника или служителя за вредите, които той е претърпял поради… невписване на прекратяването на трудовия договор по реда на чл. 62, ал. 3.' },
      { ref: 'чл. 226, ал. 3 КТ', text: 'Обезщетението по ал. 2 обхваща… брутното трудово възнаграждение от деня на прекратяването на трудовото правоотношение до вписване на прекратяването на трудовия договор по реда на чл. 62, ал. 3.' },
    ],
  });
}

// ---- Б2. Срок за изплащане на обезщетенията (чл. 228, ал. 3 КТ) -------------

export function buildPayDeadlineCalc() {
  const term = dateInput('');
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  function recompute() {
    clear(result); clear(notesBox);
    const iso = term.dataset.iso;
    if (!okIso(iso)) { result.appendChild(el('div', { class: 'calc-line', text: 'Въведете датата на прекратяването.' })); return; }
    const r = payDeadline({ terminatedOn: iso });
    result.appendChild(line('Краен срок за изплащане', bg(r.deadline), true));
    r.notes.forEach((n) => notesBox.appendChild(noteRow(n)));
  }
  term.addEventListener('input', recompute); term.addEventListener('change', recompute);
  recompute();
  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Докога се плащат обезщетенията при напускане (чл. 228, ал. 3 КТ)' }),
    hintify(field('Дата на прекратяването', term, { size: 's' }),
      'чл. 228, ал. 3 КТ (нова — ДВ, бр. 102/2017)', 'Обезщетенията при прекратяване се изплащат до последния ден на месеца, следващ месеца на прекратяването, освен ако в КТД е уговорен друг срок.'),
    result, notesBox);
  return withLaw(panel, {
    when: [
      'При всяко прекратяване с дължими обезщетения (чл. 220, 221, 222, 224) — докога фирмата трябва да ги преведе.',
      'Клиент пита „кога най-късно да платя неизползвания отпуск на напусналия" — това е срокът.',
      'След срока тече и законната лихва — датата е важна за ведомостта и за риска от иск.',
    ],
    quotes: [
      { ref: 'чл. 228, ал. 3 КТ (ДВ 107/2020)', text: 'Обезщетенията по този раздел, дължими при прекратяване на трудовото правоотношение, се изплащат не по-късно от последния ден на месеца, следващ месеца, през който правоотношението е прекратено, освен ако в колективния трудов договор е договорен друг срок. След изтичане на този срок работодателят дължи обезщетението заедно със законната лихва.' },
    ],
  });
}

// ---- Б3. Давности по трудови спорове (чл. 358 КТ) ---------------------------

export function buildClaimDeadlinesCalc() {
  const kindSel = el('select', {},
    ...Object.entries(CLAIM_KINDS).map(([v, k]) => el('option', { value: v, text: k.label, selected: v === 'dismissal' })));
  const start = dateInput('');
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  function recompute() {
    clear(result); clear(notesBox);
    const iso = start.dataset.iso;
    if (!okIso(iso)) { result.appendChild(el('div', { class: 'calc-line', text: 'Въведете началната дата (откога тече срокът).' })); return; }
    const r = claimDeadline({ kind: kindSel.value, startsOn: iso });
    result.appendChild(line('Срок', r.months === 36 ? '3 години' : r.months + (r.months === 1 ? ' месец' : ' месеца')));
    result.appendChild(line('Искът може да се предяви до', bg(r.deadline), true));
    r.notes.forEach((n) => notesBox.appendChild(noteRow(n)));
    notesBox.appendChild(noteRow('Изтеклата давност не се прилага служебно — съдът я зачита само при възражение от другата страна.'));
  }
  [kindSel, start].forEach((i) => { i.addEventListener('input', recompute); i.addEventListener('change', recompute); });
  recompute();
  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Давност по трудови спорове (чл. 358 КТ)' }),
    hintify(field('Вид на спора', kindSel),
      'чл. 358, ал. 1 КТ', 'Исковете се предявяват в 1 месец (дисциплинарни), 2 месеца (прекратяване/уволнение) или 3 години (парични вземания).'),
    field('Начална дата', start, { size: 's' }),
    result, notesBox);
  return withLaw(panel, {
    when: [
      'Служител на клиент иска да оспори уволнение — има само 2 месеца от прекратяването.',
      'Преценка докога бивш служител може да претендира неплатени суми (3 години) — т.е. докога фирмата е изложена.',
      'Работник оспорва „забележка" или заповед за ограничена имуществена отговорност — 1 месец.',
    ],
    quotes: [
      { ref: 'чл. 358, ал. 1 КТ', text: 'Исковете по трудови спорове се предявяват в следните срокове: 1. едномесечен — по спорове за ограничена имуществена отговорност на работника или служителя и за отмяна на дисциплинарно наказание „забележка"; 2. двумесечен — по спорове за отмяна на дисциплинарно наказание „предупреждение за уволнение", изменение на мястото и характера на работата и прекратяване на трудовото правоотношение; 3. тригодишен — по всички останали трудови спорове.' },
      { ref: 'чл. 358, ал. 2 КТ', text: 'Сроковете по предходната алинея започват да текат… от деня, в който правото, предмет на иска, е станало изискуемо или е могло да бъде упражнено. При парични вземания изискуемостта се смята настъпила в деня, в който по вземането е трябвало да се извърши плащане по надлежния ред.' },
    ],
  });
}

// ---- Б4. Срок за изпитване (чл. 70–71 КТ) -----------------------------------

export function buildProbationCalc() {
  const f = {
    start: dateInput(''),
    months: input({ type: 'number', step: '1', min: '1', max: '6', value: '6' }),
    fixed: el('input', { type: 'checkbox' }),
    contractMonths: input({ type: 'number', step: '1', min: '1', placeholder: 'напр. 9' }),
    absent: input({ type: 'number', step: '1', min: '0', value: '0' }),
  };
  const contractField = field('Срок на договора (месеци)', f.contractMonths, { size: 's' });
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  function recompute() {
    clear(result); clear(notesBox);
    contractField.style.display = f.fixed.checked ? '' : 'none';
    const iso = f.start.dataset.iso;
    if (!okIso(iso)) { result.appendChild(el('div', { class: 'calc-line', text: 'Въведете началото на договора.' })); return; }
    const r = probationEnd({
      startsOn: iso, months: Number(f.months.value) || 6,
      absentDays: Number(f.absent.value) || 0,
      contractMonths: f.fixed.checked ? (Number(f.contractMonths.value) || 0) : null,
    });
    result.appendChild(line('Приложим срок за изпитване', r.monthsApplied + (r.monthsApplied === 1 ? ' месец' : ' месеца')));
    result.appendChild(line('Изпитването изтича на', bg(r.endsOn), true));
    r.notes.forEach((n) => notesBox.appendChild(noteRow(n)));
  }
  Object.values(f).forEach((i) => { i.addEventListener('input', recompute); i.addEventListener('change', recompute); });
  recompute();
  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Срок за изпитване (чл. 70–71 КТ)' }),
    row(field('Начало на договора', f.start, { size: 's' }),
      hintify(field('Уговорено изпитване (месеци)', f.months, { size: 's' }),
        'чл. 70, ал. 1 КТ', 'Срокът за изпитване е до 6 месеца; в него не се включва времето в законоустановен отпуск.')),
    checkRow(f.fixed, 'Договорът е СРОЧЕН (при срок под 1 година изпитването е до 1 месец)'),
    contractField,
    field('Дни в отпуск/болничен по време на изпитването', f.absent, { size: 's' }),
    result, notesBox);
  return withLaw(panel, {
    when: [
      'Докога клиентът може да освободи нов служител без предизвестие и обезщетения (чл. 71).',
      'Проверка на клауза: при срочен договор под 1 година „6 месеца изпитване" е невалидна — важи 1 месец (изм. ДВ 62/2022).',
      'Служителят е ползвал отпуск или болничен — изпитването се удължава с тези дни.',
      'За същата работа при същия работодател изпитване се уговаря само веднъж (чл. 70, ал. 5).',
    ],
    quotes: [
      { ref: 'чл. 70, ал. 1 КТ', text: 'Окончателното приемане на работа… може да се предшествува от договор със срок за изпитване до 6 месеца, а когато за работата е определен срок, по-кратък от една година — срок за изпитване до един месец.' },
      { ref: 'чл. 70, ал. 4 КТ', text: 'В срока за изпитването не се включва времето, през което работникът или служителят е бил в законоустановен отпуск или по други уважителни причини не е изпълнявал работата, за която е сключен договорът.' },
      { ref: 'чл. 71, ал. 1 КТ', text: 'До изтичане на срока за изпитване страната, в чиято полза е уговорен, може да прекрати договора без предизвестие.' },
    ],
  });
}

// ---- В3. Майчинство и бащинство — срокове (чл. 163–164в КТ) -----------------

export function buildMaternityCalc() {
  const f = { due: dateInput(''), birth: dateInput(''), discharge: dateInput('') };
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  function recompute() {
    clear(result); clear(notesBox);
    if (!okIso(f.due.dataset.iso)) { result.appendChild(el('div', { class: 'calc-line', text: 'Въведете термина (очакваната дата на раждане).' })); return; }
    const r = maternityTimeline({
      dueDate: f.due.dataset.iso,
      birthDate: okIso(f.birth.dataset.iso) ? f.birth.dataset.iso : null,
      dischargeDate: okIso(f.discharge.dataset.iso) ? f.discharge.dataset.iso : null,
    });
    result.appendChild(line('Отпускът по чл. 163 започва (45 дни преди термина)', bg(r.leaveStart), true));
    result.appendChild(line('410-те дни изтичат на', bg(r.day410End)));
    if (r.father15End) result.appendChild(line('15-те дни на бащата (от изписването) — до', bg(r.father15End)));
    if (r.until2y) result.appendChild(line('Отглеждане до 2 г. (чл. 164) — до', bg(r.until2y)));
    if (r.father2moUntil) result.appendChild(line('2-та месеца на бащата (чл. 164в) — до', bg(r.father2moUntil)));
    r.notes.forEach((n) => notesBox.appendChild(noteRow(n)));
  }
  Object.values(f).forEach((i) => { i.addEventListener('input', recompute); i.addEventListener('change', recompute); });
  recompute();
  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Майчинство и бащинство — срокове (чл. 163–164в КТ)' }),
    row(hintify(field('Термин', f.due, { size: 's' }),
      'чл. 163, ал. 1 КТ', 'Отпуск поради бременност и раждане 410 дни за всяко дете, от които 45 дни преди раждането.'),
      field('Раждане (ако е известно)', f.birth, { size: 's' })),
    field('Изписване от болницата (за 15-те дни на бащата)', f.discharge, { size: 's' }),
    result, notesBox);
  return withLaw(panel, {
    when: [
      'Служителка на клиент излиза по майчинство — откога започва отпускът и докога трае.',
      'Планиране на заместване: 410-те дни, после евентуално отпуск до 2-годишна възраст.',
      'Бащата пита за 15-те дни след изписването и за 2-та месеца до 8-годишна възраст на детето.',
      'Парите (обезщетенията) са от ДОО през НОИ — тук се следят само сроковете.',
    ],
    quotes: [
      { ref: 'чл. 163, ал. 1 КТ', text: 'Работничката или служителката има право на отпуск поради бременност и раждане в размер 410 дни за всяко дете, от които 45 дни задължително се ползват преди раждането.' },
      { ref: 'чл. 163, ал. 8 КТ', text: 'Бащата (осиновителят) има право на 15-дневен отпуск при раждане на дете от датата на изписване на детето от лечебното заведение.' },
      { ref: 'чл. 164, ал. 1 КТ', text: 'След използване на отпуска поради бременност, раждане или осиновяване, ако детето не е настанено в детско заведение, работничката или служителката има право на допълнителен отпуск за отглеждане на дете до навършване на 2-годишната му възраст.' },
      { ref: 'чл. 164в, ал. 1 КТ', text: 'Работник или служител — баща (осиновител) на дете до 8-годишна възраст, има право на отпуск за отглеждане на детето в размер 2 месеца, когато не е ползвал отпуск по чл. 163, ал. 10, чл. 164, ал. 3, чл. 164б, ал. 2 и 5 или чл. 167, ал. 1.' },
    ],
  });
}

// ---- В4. Целеви отпуски — справочник (чл. 157 КТ) ---------------------------

export function buildTargetedLeaveCalc() {
  const items = [
    ['Встъпване в брак', '2 работни дни'],
    ['Кръводаряване', 'денят на прегледа и кръводаряването + 1 ден след него'],
    ['Смърт на родител, дете, съпруг, брат, сестра, родител на другия съпруг или роднина по права линия', '2 работни дни'],
    ['Призоваване в съд или от друг орган (страна, свидетел, вещо лице)', 'за времето на явяването'],
    ['Предизвестие от работодателя — за търсене на работа', 'по 1 час дневно за дните на предизвестието'],
  ];
  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Целеви отпуски (чл. 157 КТ) — справочник' },
      lawHint('чл. 157, ал. 1 КТ', 'Работодателят е длъжен да освобождава от работа при брак, кръводаряване, смърт на близък, призоваване в съд — в посочените размери.')),
    el('div', { class: 'calc-result' }, ...items.map(([ev, right]) => line(ev, right))),
    el('div', { style: 'margin-top:8px' },
      noteRow('Работодателят е ДЛЪЖЕН да освободи работника — това не се удържа от платения годишен отпуск.'),
      noteRow('Възнаграждение за тези дни се дължи според КТД или споразумение между страните (чл. 157, ал. 3) — без уговорка отпускът може да е неплатен, но осигурителни последици няма.'),
      noteRow('При смърт на близък дните вече може да се ползват наведнъж или на части до 8 дни от събитието (изм. на НРВПО, 2025).')));
  return withLaw(panel, {
    when: [
      'Служител се жени, дарява кръв, има погребение или е призован в съд — колко дни дължи работодателят.',
      'ТРЗ обработва отсъствието: то е отделно основание, не се взима от платения годишен отпуск.',
      'Клиент пита „плаща ли се" — зависи от КТД/споразумението (чл. 157, ал. 3).',
    ],
    quotes: [
      { ref: 'чл. 157, ал. 1 КТ', text: 'Работодателят е длъжен да освобождава от работа работника или служителя: 1. при встъпване в брак — 2 работни дни; 2. при кръводаряване — за деня на прегледа и кръводаряването, както и 1 ден след него; 3. при смърт на родител, дете, съпруг, брат, сестра и родител на другия съпруг или други роднини по права линия — 2 работни дни…' },
      { ref: 'чл. 157, ал. 3 КТ', text: 'През време на отпуските по ал. 1 на работника или служителя се изплаща възнаграждение според предвиденото в колективния трудов договор или по споразумение между работника или служителя и работодателя.' },
    ],
  });
}

// ---- В5. Неплатен отпуск и стаж (чл. 160 КТ) --------------------------------

export function buildUnpaidLeaveCalc() {
  const daysI = input({ type: 'number', step: '1', min: '0', placeholder: 'напр. 45' });
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  function recompute() {
    clear(result); clear(notesBox);
    const d = Number(daysI.value) || 0;
    if (d <= 0) { result.appendChild(el('div', { class: 'calc-line', text: 'Въведете работните дни неплатен отпуск за годината.' })); return; }
    const r = unpaidLeaveSplit({ workDays: d });
    result.appendChild(line('Зачита се за трудов стаж', r.counted + ' раб. дни', true));
    result.appendChild(line('НЕ се зачита', r.notCounted + ' раб. дни'));
    notesBox.appendChild(noteRow('Лимитът е за календарна година. Над 30-те дни стажът се зачита само ако закон или акт на МС го предвижда изрично.'));
    notesBox.appendChild(noteRow('През неплатения отпуск здравните вноски са за сметка на служителя — напомнете на клиента да ги уреди.'));
  }
  daysI.addEventListener('input', recompute);
  recompute();
  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Неплатен отпуск и стаж (чл. 160 КТ)' }),
    hintify(field('Неплатен отпуск през годината (раб. дни)', daysI, { size: 's' }),
      'чл. 160, ал. 3 КТ', 'Неплатеният отпуск до 30 работни дни в една календарна година се признава за трудов стаж, а над 30 — само ако това е предвидено в закон.'),
    result, notesBox);
  return withLaw(panel, {
    when: [
      'Служител иска дълъг неплатен отпуск — клиентът трябва да знае колко от него е стаж.',
      'Попълване на УП-3/справки за стаж при минали неплатени отпуски.',
      'Разговор за осигуровките: през неплатения здравната вноска е за сметка на служителя.',
    ],
    quotes: [
      { ref: 'чл. 160, ал. 1 КТ', text: 'Работодателят по искане на работника или служителя може да му разреши неплатен отпуск независимо от това, дали е ползувал или не платения си годишен отпуск и независимо от продължителността на трудовия му стаж.' },
      { ref: 'чл. 160, ал. 3 КТ', text: 'Неплатеният отпуск до 30 работни дни в една календарна година се признава за трудов стаж, а над 30 работни дни — само ако това е предвидено в този кодекс, в друг закон или в акт на Министерския съвет.' },
    ],
  });
}

// ---- Г2. Нощен труд (чл. 140, 261 КТ + чл. 8 НСОРЗ) -------------------------

export function buildNightWorkCalc() {
  const f = {
    date: dateInput(localTodayIso()),
    from: input({ type: 'text', value: '22:00', placeholder: 'ЧЧ:ММ' }),
    to: input({ type: 'text', value: '06:00', placeholder: 'ЧЧ:ММ' }),
    shifts: input({ type: 'number', step: '1', min: '1', value: '1' }),
  };
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  const okTime = (s) => /^\d{1,2}:\d{2}$/.test(s || '');
  function recompute() {
    clear(result); clear(notesBox);
    if (!okIso(f.date.dataset.iso) || !okTime(f.from.value) || !okTime(f.to.value)) {
      result.appendChild(el('div', { class: 'calc-line', text: 'Попълнете дата и час на смяната (напр. 22:00 – 06:00).' }));
      return;
    }
    let u;
    try { u = nightUplift({ date: f.date.dataset.iso }); } catch (e) {
      result.appendChild(el('div', { class: 'calc-hint-line', text: '⚠ ' + e.message })); return;
    }
    const perShift = nightHoursInShift({ from: f.from.value, to: f.to.value });
    const shifts = Number(f.shifts.value) || 1;
    const totalHours = perShift * shifts;
    result.appendChild(line('Нощни часове в смяната (22:00–06:00)', String(perShift)));
    if (shifts > 1) result.appendChild(line('Общо нощни часове (' + shifts + ' смени)', String(totalHours)));
    result.appendChild(line('Добавка на нощен час' + (u.floorApplied ? ' (долният праг)' : ' (0,15% от МРЗ)'), eur(u.perHour)));
    result.appendChild(line('Добавка за нощния труд', eur(u.pay(totalHours)), true));
    notesBox.appendChild(noteRow(u.source));
    notesBox.appendChild(noteRow('Добавката е ОТГОРЕ върху възнаграждението за часовете; нормата нощем е 7 ч. (35 ч. седмично) — при пълна нощна смяна има и преизчисляване.'));
    notesBox.appendChild(noteRow('При СИРВ нощните часове се превръщат в дневни с коефициент 8/7 — виж калкулатора „СИРВ".'));
  }
  Object.values(f).forEach((i) => { i.addEventListener('input', recompute); i.addEventListener('change', recompute); });
  recompute();
  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Нощен труд — добавка (чл. 140, 261 КТ)' }),
    row(field('Към дата (за МРЗ)', f.date, { size: 's' }), field('Брой смени', f.shifts, { size: 's' })),
    row(hintify(field('Смяната започва', f.from, { size: 's' }),
      'чл. 140, ал. 2 КТ', 'Нощен е трудът между 22:00 и 06:00 ч.; добавката е по чл. 261 КТ и НСОРЗ чл. 8.'),
      field('Смяната свършва', f.to, { size: 's' })),
    result, notesBox);
  return withLaw(panel, {
    when: [
      'Клиент има охрана, производство на смени, заведение — колко струва нощният час.',
      'Проверка на ведомост: платена ли е поне минималната добавка за всеки час между 22:00 и 06:00.',
      'Смяната минава през полунощ — панелът сам изчислява колко от часовете са нощни.',
    ],
    quotes: [
      { ref: 'чл. 140, ал. 2 КТ', text: 'Нощен е трудът, който се полага от 22,00 ч. до 06,00 ч.' },
      { ref: 'чл. 261 КТ', text: 'Положеният нощен труд се заплаща с увеличение, уговорено от страните по трудовото правоотношение, но не по-малко от размерите, определени от Министерския съвет.' },
      { ref: 'чл. 8 НСОРЗ (изм. ДВ 105/2023)', text: 'За всеки отработен нощен час или част от него между 22,00 ч. и 6,00 ч. на работниците и служителите се заплаща допълнително трудово възнаграждение за нощен труд в размер не по-малък от 0,15 на сто от минималната работна заплата, установена за страната, но не по-малко от един лев.' },
    ],
  });
}

// ---- Г3. СИРВ — норма и извънредни (чл. 142 КТ) -----------------------------

export function buildSirvCalc() {
  const now = new Date();
  const f = {
    year: input({ type: 'number', step: '1', min: '2000', max: '2100', value: String(now.getFullYear()) }),
    monthFrom: input({ type: 'number', step: '1', min: '1', max: '12', value: String(now.getMonth() + 1) }),
    monthTo: input({ type: 'number', step: '1', min: '1', max: '12', value: String(now.getMonth() + 1) }),
    worked: input({ type: 'number', step: '0.5', min: '0', value: '' }),
    night: input({ type: 'number', step: '0.5', min: '0', value: '0' }),
  };
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  function recompute() {
    clear(result); clear(notesBox);
    const year = Number(f.year.value), mf = Number(f.monthFrom.value), mt = Number(f.monthTo.value);
    if (!(year >= 2000) || !(mf >= 1) || !(mt >= mf) || mt > 12) {
      result.appendChild(el('div', { class: 'calc-line', text: 'Задайте периода на сумиране (година, от месец, до месец).' }));
      return;
    }
    const r = sirvBalance({ year, monthFrom: mf, monthTo: mt, workedHours: Number(f.worked.value) || 0, nightHours: Number(f.night.value) || 0 });
    result.appendChild(line('Работни дни в периода', String(r.workDays)));
    result.appendChild(line('Норма (по календаря)', r.normHours + ' ч.'));
    if (r.convertedNight) result.appendChild(line('Нощни, превърнати с 8/7', r.convertedNight + ' ч.'));
    result.appendChild(line('Отработени (с превръщането)', r.totalWorked + ' ч.'));
    result.appendChild(line('Извънреден труд над нормата', r.overtime + ' ч.', true));
    r.notes.forEach((n) => notesBox.appendChild(noteRow('⚠ ' + n)));
    notesBox.appendChild(noteRow('Извънредните при СИРВ се заплащат с не по-малко от 50% увеличение (чл. 262, ал. 1, т. 4) — сметни ги в „Извънреден труд".'));
    notesBox.appendChild(noteRow('Периодът на сумиране се определя предварително със заповед; нормата се намалява при отпуск/болничен на служителя.'));
  }
  Object.values(f).forEach((i) => { i.addEventListener('input', recompute); i.addEventListener('change', recompute); });
  recompute();
  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'СИРВ — норма и извънредни часове (чл. 142 КТ)' }),
    row(field('Година', f.year, { size: 's' }), field('От месец', f.monthFrom, { size: 's' }), field('До месец (вкл.)', f.monthTo, { size: 's' })),
    row(hintify(field('Отработени часове (дневни)', f.worked, { size: 's' }),
      'чл. 142, ал. 2 КТ', 'Работодателят може да установи сумирано изчисляване на работното време — седмично, месечно или за друг календарен период до 4 месеца.'),
      field('От тях нощни часове', f.night, { size: 's' })),
    result, notesBox);
  return withLaw(panel, {
    when: [
      'Клиент работи на графици/смени със сумирано изчисляване — колко е нормата за периода и има ли извънредни.',
      'Приключване на период на СИРВ: часовете над нормата се плащат като извънреден труд с 50%.',
      'Нощните смени при СИРВ: часовете се превръщат в дневни с 8/7 преди сравнението с нормата.',
    ],
    quotes: [
      { ref: 'чл. 142, ал. 2 КТ', text: 'Работодателят може да установи сумирано изчисляване на работното време — седмично, месечно или за друг календарен период, който не може да бъде повече от 4 месеца.' },
      { ref: 'чл. 142, ал. 4 КТ', text: 'С колективен трудов договор… може да бъде определен период за сумирано изчисляване на работното време до 12 месеца.' },
      { ref: 'чл. 9а НРВПО (по смисъл)', text: 'При сумирано изчисляване нощните часове се превръщат в дневни с коефициент, равен на отношението между нормалната продължителност на дневното и нощното работно време (8/7).' },
    ],
  });
}

// ---- Г5. Труд на официален празник (чл. 264 КТ) -----------------------------

export function buildHolidayWorkCalc() {
  const f = {
    hours: input({ type: 'number', step: '0.5', min: '0', placeholder: 'напр. 8' }),
    rate: input({ type: 'number', step: '0.01', min: '0', placeholder: 'часова ставка' }),
    overtime: el('input', { type: 'checkbox' }),
  };
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  function recompute() {
    clear(result); clear(notesBox);
    const h = Number(f.hours.value) || 0, rate = Number(f.rate.value) || 0;
    if (h <= 0 || rate <= 0) { result.appendChild(el('div', { class: 'calc-line', text: 'Въведете часовете и часовата ставка.' })); return; }
    const r = holidayWorkPay({ hours: h, rate, alsoOvertime: f.overtime.checked });
    result.appendChild(line('Единично (часове × ставка)', eur(r.base)));
    result.appendChild(line('Удвоено по чл. 264', eur(r.doubled)));
    result.appendChild(line('Дължимо общо', eur(r.total), true));
    r.notes.forEach((n) => notesBox.appendChild(noteRow(n)));
    notesBox.appendChild(noteRow('Часовата ставка от месечна заплата: сметни я в „Извънреден труд" (заплата ÷ часовете на месеца).'));
  }
  Object.values(f).forEach((i) => { i.addEventListener('input', recompute); i.addEventListener('change', recompute); });
  recompute();
  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Работа на официален празник (чл. 264 КТ)' }),
    row(hintify(field('Отработени часове на празника', f.hours, { size: 's' }),
      'чл. 264 КТ', 'За работа през дните на официалните празници се заплаща според уговореното, но не по-малко от удвоения размер на трудовото възнаграждение.'),
      field('Часова ставка', f.rate, { size: 's' })),
    checkRow(f.overtime, 'Трудът е и ИЗВЪНРЕДЕН (извън графика) — добавя се и 100% по чл. 262'),
    result, notesBox);
  return withLaw(panel, {
    when: [
      'Магазин, заведение или производство работи на 3 март, Великден, Коледа… — колко се дължи за тези часове.',
      'Работата на празник по ГРАФИК не е извънреден труд, но пак се плаща поне двойно.',
      'Ако е извън графика (извънреден труд) — двете увеличения се кумулират (практика на ВКС): общо тройно.',
    ],
    quotes: [
      { ref: 'чл. 264 КТ', text: 'За работа през дните на официалните празници, независимо от това, дали представлява извънреден труд или не, на работника или служителя се заплаща според уговореното, но не по-малко от удвоения размер на трудовото му възнаграждение.' },
      { ref: 'чл. 262, ал. 1, т. 3 КТ', text: 'Положеният извънреден труд се заплаща с увеличение, уговорено между работника или служителя и работодателя, но не по-малко от… 100 на сто — за работа през дните на официалните празници.' },
    ],
  });
}

// ---- Г6. Болничен — първите дни от работодателя (чл. 40, ал. 5 КСО) ---------

export function buildSickDaysCalc() {
  const f = { from: dateInput(''), to: dateInput(''), daily: input({ type: 'number', step: '0.01', min: '0', placeholder: 'напр. 60' }) };
  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  function recompute() {
    clear(result); clear(notesBox);
    const from = f.from.dataset.iso, to = f.to.dataset.iso;
    if (!okIso(from) || !okIso(to) || from > to || !(Number(f.daily.value) > 0)) {
      result.appendChild(el('div', { class: 'calc-line', text: 'Попълнете периода на болничния и среднодневното УГОВОРЕНО възнаграждение.' }));
      return;
    }
    let r;
    try { r = employerSickPay({ from, to, dailyAgreed: Number(f.daily.value) }); } catch (e) {
      result.appendChild(el('div', { class: 'calc-hint-line', text: '⚠ ' + e.message })); return;
    }
    result.appendChild(line('Дни за сметка на работодателя', r.days.map(bg).join(' и ') || '— (няма работни дни в периода)'));
    result.appendChild(line('Процент', r.percent + '% от среднодневното уговорено'));
    result.appendChild(line('Работодателят плаща', eur(r.amount), true));
    r.notes.forEach((n) => notesBox.appendChild(noteRow(n)));
  }
  Object.values(f).forEach((i) => { i.addEventListener('input', recompute); i.addEventListener('change', recompute); });
  recompute();
  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Болничен — дните за сметка на работодателя (чл. 40, ал. 5 КСО)' }),
    row(hintify(field('Болничният от', f.from, { size: 's' }),
      'чл. 40, ал. 5 КСО (изм., от 01.01.2024)', 'Осигурителят изплаща на лицето за първите ДВА работни дни от неработоспособността 70 на сто от среднодневното брутно възнаграждение за месеца.'),
      field('до (вкл.)', f.to, { size: 's' })),
    field('Среднодневно УГОВОРЕНО възнаграждение', f.daily, { size: 's' }),
    result, notesBox);
  return withLaw(panel, {
    when: [
      'Служител на клиент представя болничен лист — какво плаща фирмата и какво поема НОИ.',
      'От 01.01.2024 работодателят дължи първите ДВА работни дни (не три, както беше преди).',
      'Уикенди и празници в началото на болничния не се броят — панелът намира точно кои дни са работни.',
      'От третия работен ден обезщетението е от НОИ — 80% от осигурителния доход (друга база, не се смята тук).',
    ],
    quotes: [
      { ref: 'чл. 40, ал. 5 КСО (в сила от 01.01.2024)', text: 'Осигурителят изплаща на осигуреното лице за първите два работни дни от временната неработоспособност 70 на сто от среднодневното брутно възнаграждение за месеца, в който е настъпила временната неработоспособност, но не по-малко от 70 на сто от среднодневното уговорено възнаграждение.' },
    ],
  });
}
